// iOS native signaling/audio. The web page is a view of this session, never its audio owner.
import { APP_PLATFORM, appPrefs } from './native.js';
const cap = window.Capacitor;
export const NATIVE_AUDIO = APP_PLATFORM === 'ios' && !!cap?.isPluginAvailable?.('NativeTalk');
const call = (method, options = {}) => cap.nativePromise('NativeTalk', method, options);

export class NativeSessionClient {
  constructor({ token, sessionId }) {
    this.token = token; this.sessionId = sessionId;
    this.nativeAudio = true;
    this.handlers = new Map();
    this.state = { channels: [], roles: [], peers: [], program: {} };
    this.status = { ws: false, dc: false, media: 'idle' };
    this.tally = { cams: [] }; this.me = null; this.session = null;
    this.talking = new Set(); this.closed = false;
    this.vol = JSON.parse(localStorage.getItem('talk.vol') || '{}');
    this._ducking = true; this.supportsSquelch = false; this.supportsProgramBuffer = false;
    this.programBuffer = 0; this.squelch = 0; this.gateOpen = true; this.level = 0;
    this.micTrack = null;
  }
  on(type, fn) {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type).add(fn);
    return () => this.handlers.get(type)?.delete(fn);
  }
  emit(type, payload) { for (const fn of this.handlers.get(type) || []) fn(payload); }
  async start({ withMic = true } = {}) {
    this.closed = false;
    this.starting = true;
    if (!this.callback) {
      this.callback = await cap.nativeCallback('NativeTalk', 'addListener', { eventName: 'event' }, ({ type, data }) => {
        if (this.closed || this.starting) return;
        if (type === 'snapshot') this.apply(data);
        else if (type === 'joined') { this.session = data; this.emit('joined', data); }
        else if (['ended', 'kicked', 'fatal'].includes(type)) {
          this.closed = true; this.talking.clear(); this.emit('talk', []);
          this.emit(type, data.message);
        } else this.emit(type, data);
      });
    }
    const base = location.pathname.replace(/[^/]*$/, '');
    const url = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${base}ws`;
    const snapshot = await call('start', { url, token: this.token, sessionId: this.sessionId, withMic,
      options: { handsFree: appPrefs.handsFree, volumes: this.vol, ducking: this._ducking } });
    this.handle = null; this.starting = false; this.apply(snapshot);
    this.micTrack = withMic ? { readyState: 'live', enabled: false } : null;
  }
  apply(snapshot) {
    if (!snapshot || (this.handle && snapshot.handle && snapshot.handle !== this.handle)) return;
    const previous = { me: JSON.stringify(this.me), state: JSON.stringify(this.state), tally: JSON.stringify(this.tally),
      talk: [...this.talking].sort().join(','), status: JSON.stringify(this.status), video: this.hasVideo };
    this.handle = snapshot.handle || this.handle;
    this.peerId = snapshot.peerId; this.session = snapshot.session || this.session;
    this.state = snapshot.state || this.state; this.me = snapshot.me?.id ? snapshot.me : this.me;
    this.tally = snapshot.tally || this.tally; this.status = snapshot.status || this.status;
    this.talking = new Set(snapshot.talking || []); this.canSwitch = !!snapshot.canSwitch;
    this.path = snapshot.path; this.level = snapshot.level || 0;
    this.hasVideo = !!snapshot.video; this.videoQuality = snapshot.videoQuality === 'off' ? null : snapshot.videoQuality;
    this.receiveMuted = !!snapshot.receiveMuted;
    if (snapshot.error && snapshot.error !== this.lastError) this.emit('error', snapshot.error);
    this.lastError = snapshot.error || '';
    if (this.micTrack) this.micTrack.enabled = this.talking.size > 0;
    if (previous.me !== JSON.stringify(this.me) && this.me) this.emit('me', this.me);
    if (previous.state !== JSON.stringify(this.state)) this.emit('state', this.state);
    if (previous.tally !== JSON.stringify(this.tally)) this.emit('tally', this.tally);
    if (previous.talk !== [...this.talking].sort().join(',')) this.emit('talk', [...this.talking]);
    if (previous.status !== JSON.stringify(this.status)) this.emit('status', this.status);
    if (!this.legacyVideoEvents && previous.video !== this.hasVideo) this.emit('video', this.hasVideo ? { native: true } : null);
    if (this.session?.name) this.emit('joined', this.session);
    this.updateVideo?.();
  }
  async request(method, data = {}) {
    return call('request', { handle: this.handle, method, data });
  }
  setTalk(target, enabled) {
    // UI reflects the native/server acknowledgement, never an optimistic transmitting state.
    if (!enabled && this.talking.has(target)) {
      this.talking.delete(target); this.emit('talk', [...this.talking]);
    }
    return call('setTalk', { handle: this.handle, target, enabled }).then((s) => this.apply(s))
      .catch((e) => { if (!this.closed) this.emit('error', e.message); });
  }
  async setListen(channelId, on) {
    await this.request('listen', { channelId, on });
    await this.recover();
  }
  volume(key) { return this.vol[key] || { v: key === '__program' ? 0.6 : 0.8, muted: false }; }
  setVolume(key, patch) {
    this.vol[key] = { ...this.volume(key), ...patch };
    localStorage.setItem('talk.vol', JSON.stringify(this.vol));
    this.configure({ volumes: this.vol });
  }
  get ducking() { return this._ducking; }
  set ducking(value) { this._ducking = value; this.configure({ ducking: value }); }
  configure(options) {
    if (this.handle && !this.closed) call('configure', { handle: this.handle, options }).catch((e) => this.emit('error', e.message));
  }
  micLevel() { return this.level; }
  setSquelch(db) { return db ? Promise.reject(new Error('Native audio uses automatic noise suppression.')) : Promise.resolve(); }
  setProgramBuffer() {}
  setOutput() { return Promise.reject(new Error('Choose the audio output in iOS Control Center.')); }
  async setProgramVideo(quality) { await this.request('programVideo', { quality }); this.videoQuality = quality === 'off' ? null : quality; }
  signal(target) { return this.request('signal', { target }); }
  adminSetRole(peerId, roleId) { return this.request('adminSetRole', { peerId, roleId }); }
  adminSetCam(peerId, camId) { return this.request('adminSetCam', { peerId, camId }); }
  adminKick(peerId) { return this.request('adminKick', { peerId }); }
  // Session route switching remains explicit until the native transport implements warm standby.
  switchPath(path) { return this.request('switchPath', { path }).catch((e) => this.emit('error', e.message)); }
  background() {} // Native lifecycle owns audio; backgrounding the web page never closes the session.
  async recover() {
    if (!this.closed) this.apply(await call('snapshot'));
    this.configure({ handsFree: appPrefs.handsFree });
  }
  leaveNow() { void this.leave(); }
  async leave() {
    if (this.closed && !this.callback) return;
    this.closed = true; this.talking.clear();
    await call('stop');
    if (this.callback) {
      cap.nativeCallback('NativeTalk', 'removeListener', { callbackId: this.callback, eventName: 'event' });
      this.callback = null;
    }
    this.unmountVideo?.();
  }

  mountVideo(video, root) {
    let frame = 0;
    const send = () => {
      frame = 0;
      if (!this.handle || this.closed) return;
      const rect = video.getBoundingClientRect();
      const clip = root.querySelector('.st-body')?.getBoundingClientRect() || rect;
      const visible = this.hasVideo && root.contains(video) && !document.querySelector('.modal') && rect.width > 0 && rect.height > 0;
      call('configure', { handle: this.handle, videoFrame: { visible, x: rect.x, y: rect.y, width: rect.width, height: rect.height,
        clipX: clip.x, clipY: clip.y, clipWidth: clip.width, clipHeight: clip.height } }).catch(() => {});
    };
    const update = () => { if (!frame) frame = requestAnimationFrame(send); };
    document.addEventListener('scroll', update, true); window.addEventListener('resize', update);
    const resize = new ResizeObserver(update); resize.observe(video);
    const mutation = new MutationObserver(update); mutation.observe(document.body, { childList: true, subtree: true });
    this.updateVideo = update;
    this.unmountVideo = () => {
      cancelAnimationFrame(frame); resize.disconnect(); mutation.disconnect();
      document.removeEventListener('scroll', update, true); window.removeEventListener('resize', update);
    };
  }
}
